'use client';

/**
 * The public "Preview" section on a product page.
 *
 * A preview is the seller's free sample: one file, world-readable, shown to
 * anyone who opens the product page whether they have bought it or not. The
 * whole design of this component follows from that, and from the fact that
 * nothing in this repo scans an upload for malware. See the header of
 * src/lib/product-preview.ts for the full reasoning; the parts that live here
 * are these.
 *
 * There is no download affordance, in any format. The bytes are only ever
 * rendered inline, and the media elements carry controlsList="nodownload" so
 * the browser's own control bar does not quietly reintroduce one. That
 * attribute removes a UI affordance and nothing more: the storage URL stays
 * publicly fetchable by anyone who reads it out of the page source, so it is a
 * statement of intent rather than a control.
 *
 * PDF is the only framed type and it is framed straight from the Firebase
 * Storage download URL, a different origin from supa.place. It carries NO
 * sandbox attribute, which is a deliberate reversal: Chromium renders a PDF
 * through a viewer the sandbox classifies as a plugin, and the sandbox has no
 * token that turns plugins back on, so every value of the attribute made Chrome
 * and Edge refuse the document and paint their own "blocked" panel where the
 * sample should have been. What protects the frame instead is set out on the
 * element itself.
 *
 * Nothing loads until the visitor asks for it. The collapsed card renders a
 * placeholder with no iframe src and no media src attached, so a 50MB sample on
 * the most-visited page in the app costs no egress on a view that never looks
 * at it. Both the placeholder and the expand button count as that ask.
 *
 * Rendering is gated upstream by resolveProductPreview(), the single predicate
 * that decides a preview exists at all. This component assumes it has already
 * run and takes a resolved value.
 *
 * `pendingUpload` is the one concession to the seller's editor. A file picked
 * but not yet saved has a `blob:` src, and the CSP lists `blob:` in img-src but
 * NOT in media-src or frame-src, so a video, an audio file or a PDF in that
 * state cannot be played or framed no matter what this component does. Rather
 * than offer a "Play preview" button that loads a black box and says nothing,
 * that combination renders a static panel that tells the seller the file plays
 * once saved. Images are unaffected and still load from the object URL.
 */

import { useCallback, useRef, useState, type SyntheticEvent } from 'react';
import Image from 'next/image';
import { FileText, Image as ImageIcon, Maximize, Music, Play, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { skipImageOptimizer } from '@/lib/image-optimizer';
import { formatFileSize } from '@/lib/product-file-pool';
import { previewKind, type PreviewKind, type ProductPreview as ProductPreviewData } from '@/lib/product-preview';

const KIND_LABEL: Record<PreviewKind, string> = {
  pdf: 'PDF',
  video: 'Video',
  audio: 'Audio',
  image: 'Image',
};

// What the centre affordance offers to do. Worded as the action rather than the
// format so the placeholder reads as a button and not as a caption.
const KIND_ACTION: Record<PreviewKind, string> = {
  pdf: 'Open preview',
  video: 'Play preview',
  audio: 'Play preview',
  image: 'View preview',
};

// Typed off one icon because every lucide export shares the same component
// type and lucide-react does not export a name for it.
const KIND_ICON: Record<PreviewKind, typeof FileText> = {
  pdf: FileText,
  video: Play,
  audio: Music,
  image: ImageIcon,
};

// What the card says INSTEAD of an action when the bytes cannot be reached yet.
// Phrased as a statement about the product page, not about the CSP: the seller
// needs to know the file is fine and will play for buyers, which is the only
// part of this they can act on.
const KIND_PENDING: Record<PreviewKind, string> = {
  pdf: 'Opens on your product page once you save.',
  video: 'Plays on your product page once you save.',
  audio: 'Plays on your product page once you save.',
  image: '',
};

interface ProductPreviewProps {
  preview: ProductPreviewData;
  productName: string;
  /**
   * The src is a local object URL for a file the seller has picked but not yet
   * saved. Only the two product editors pass this; the buyer's product page
   * always has a stored https src and leaves it false.
   */
  pendingUpload?: boolean;
}

export default function ProductPreview({ preview, productName, pendingUpload = false }: ProductPreviewProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // The media element exists in exactly one place at a time: in the card, or in
  // the dialog, never both. Two live elements would mean expanding a video left
  // a second copy playing audio behind the backdrop. The cost is that React
  // unmounts and remounts the element as it moves between the two surfaces,
  // which resets the playhead, so the last known position is stashed here and
  // restored once the new element reports its metadata. A PDF iframe cannot be
  // treated the same way: it reloads and returns to page one on expand, which
  // is accepted rather than worked around.
  const playheadRef = useRef(0);

  const rememberPlayhead = useCallback((event: SyntheticEvent<HTMLMediaElement>) => {
    playheadRef.current = event.currentTarget.currentTime;
  }, []);

  const restorePlayhead = useCallback((event: SyntheticEvent<HTMLMediaElement>) => {
    if (playheadRef.current > 0) event.currentTarget.currentTime = playheadRef.current;
  }, []);

  // Expanding is itself a deliberate request for the content, so it satisfies
  // the click-to-load rule on its own and the visitor is not made to click the
  // placeholder a second time inside the dialog.
  const expand = useCallback(() => {
    setIsLoaded(true);
    setIsExpanded(true);
  }, []);

  const kind = previewKind(preview.contentType);
  // Unreachable through resolveProductPreview, which already refuses a preview
  // this build has no renderer for. Kept because the component is exported and
  // a future caller could reach it another way, and because it narrows `kind`.
  if (!kind) return null;

  const KindIcon = KIND_ICON[kind];
  const label = KIND_LABEL[kind];
  const displayName = preview.name || `${productName} preview`;
  const humanSize = preview.size > 0 ? formatFileSize(preview.size) : '';

  // An unsaved pick that the browser will refuse to play or frame. Images are
  // excluded because img-src does list `blob:`, so they render normally.
  const awaitingUpload = pendingUpload && kind !== 'image';

  // Same furniture as the placeholder below, minus the affordance: a card that
  // cannot load anything must not look like one that can.
  const pendingPanel = (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center">
      <span className="flex h-14 w-14 items-center justify-center rounded-full border bg-background/80">
        <KindIcon className="h-6 w-6 text-muted-foreground" aria-hidden />
      </span>
      <span className="max-w-full truncate text-sm font-medium">{displayName}</span>
      <span className="text-xs text-muted-foreground">
        {humanSize ? `${label} · ${humanSize}` : label}
      </span>
      <span className="max-w-xs text-xs text-muted-foreground">{KIND_PENDING[kind]}</span>
    </div>
  );

  const placeholder = (
    <button
      type="button"
      onClick={() => setIsLoaded(true)}
      className="group absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center transition-colors hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
      aria-label={`${KIND_ACTION[kind]} for ${productName}`}
    >
      <span className="flex h-14 w-14 items-center justify-center rounded-full border bg-background/80 transition-transform group-hover:scale-105">
        <KindIcon className="h-6 w-6" aria-hidden />
      </span>
      <span className="max-w-full truncate text-sm font-medium">{displayName}</span>
      {/* Badge renders a div, which a button element may not contain, so the
          type chip here is plain phrasing content. */}
      <span className="text-xs text-muted-foreground">
        {humanSize ? `${label} · ${humanSize}` : label}
      </span>
      <span className="text-xs font-medium text-muted-foreground group-hover:text-foreground">{KIND_ACTION[kind]}</span>
    </button>
  );

  // The URL the PDF frame actually loads.
  //
  // `#toolbar=0` asks Chromium's viewer to render the document without its own
  // toolbar, whose Download and Print buttons are precisely the affordance this
  // section refuses to carry. It has the same standing as controlsList=
  // "nodownload" on the media elements below: a statement of intent rather than
  // a control, because the storage URL is public to anyone who reads it out of
  // the page source. Firefox ignores the parameter and keeps its own toolbar.
  //
  // Any fragment already on the src is dropped rather than appended to: a URL
  // carries at most one, and this src is seller-supplied.
  const pdfSrc = `${preview.src.split('#')[0]}#toolbar=0`;

  // `expanded` changes only the responsive image hints. Both surfaces are
  // position:relative boxes, so the same absolute layout works in the card and
  // in the dialog.
  const renderMedia = (expanded: boolean) => {
    if (awaitingUpload) return pendingPanel;
    if (!isLoaded) return placeholder;

    switch (kind) {
      case 'pdf':
        return (
          <iframe
            src={pdfSrc}
            title={`${productName} preview`}
            className="absolute inset-0 h-full w-full border-0 bg-white"
            // NO sandbox attribute, and its absence is the whole fix.
            //
            // This frame carried sandbox="allow-scripts", and every seller and
            // buyer on Chrome or Edge got the browser's own "blocked" panel
            // instead of the sample. Chromium hands a PDF to a viewer that the
            // sandbox classifies as a plugin, and the sandbox has no token that
            // turns plugins back on. So the old value was not too narrow:
            // allow-same-origin, allow-popups, any combination at all blocks it
            // the same way, because the PRESENCE of the attribute is what does
            // the blocking. Firefox renders the same file, its viewer being
            // ordinary script rather than a plugin, which is why this read as a
            // browser quirk rather than as the attribute.
            //
            // That leaves the choice as: show the preview, or ship the
            // attribute. What holds the line without it:
            //
            //   - The frame is CROSS-ORIGIN and stays so. The bytes come from
            //     firebasestorage.googleapis.com, so the framed document cannot
            //     read this page's DOM, our Firebase auth token or our
            //     IndexedDB, sandbox or no sandbox. That separation was always
            //     the real protection; the sandbox sat on top of it.
            //   - The response can only BE a PDF. storage.rules pins the
            //     content type of everything under products/{id}/preview/ to
            //     PREVIEW_ALLOWED_MIME_TYPES on create AND on update, so the
            //     object cannot be re-labelled after it lands, and
            //     inspectPreviewObject in functions re-reads the object's real
            //     metadata after the write and strips the field when it
            //     disagrees. An HTML document, which is the thing that would
            //     actually run script here, cannot reach the far end of this
            //     frame.
            //   - `allow=""` delegates no Permissions Policy features, so
            //     camera, microphone, geolocation and the rest stay unavailable
            //     to the frame whatever the browser defaults become.
            //   - Nothing loads until the visitor asks. The placeholder is what
            //     attaches this src at all.
            //
            // What is genuinely given up: a link annotation inside a hostile
            // PDF can now open a tab the way a PDF link does anywhere else, and
            // with a click it could navigate the top window. The bytes are
            // parsed by the browser's own PDF viewer in its own process either
            // way, which is the same exposure as opening any PDF link.
            allow=""
            referrerPolicy="no-referrer"
          />
        );

      case 'video':
        return (
          <video
            src={preview.src}
            controls
            controlsList="nodownload"
            preload="none"
            playsInline
            onTimeUpdate={rememberPlayhead}
            onLoadedMetadata={restorePlayhead}
            aria-label={`${productName} video preview`}
            className="absolute inset-0 h-full w-full bg-black object-contain"
          />
        );

      case 'audio':
        return (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-6">
            <Music className="h-10 w-10 text-muted-foreground" aria-hidden />
            <p className="max-w-full truncate text-sm font-medium">{displayName}</p>
            <audio
              src={preview.src}
              controls
              controlsList="nodownload"
              preload="none"
              onTimeUpdate={rememberPlayhead}
              onLoadedMetadata={restorePlayhead}
              aria-label={`${productName} audio preview`}
              className="w-full max-w-md"
            />
          </div>
        );

      case 'image':
        return (
          <Image
            src={preview.src}
            alt={`${productName} preview`}
            fill
            sizes={expanded ? '100vw' : '(max-width: 768px) 100vw, 50vw'}
            unoptimized={skipImageOptimizer(preview.src)}
            className="object-contain"
          />
        );
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between py-3">
          <CardTitle>Preview</CardTitle>
          {/* Expanding an unplayable pick would only make the black box bigger,
              so the control goes away with the affordance it belongs to. */}
          <Button
            variant="outline"
            size="icon"
            onClick={expand}
            disabled={awaitingUpload}
            aria-label={`Open the preview for ${productName} fullscreen`}
          >
            <Maximize className="h-5 w-5" aria-hidden />
          </Button>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-muted">
            {isExpanded ? (
              // The one element has moved into the dialog. The card keeps its
              // footprint so closing the dialog does not shift the page.
              <p className="absolute inset-0 flex items-center justify-center p-6 text-center text-sm text-muted-foreground">
                Showing in the expanded view.
              </p>
            ) : (
              renderMedia(false)
            )}
          </div>
        </CardContent>
      </Card>

      {isExpanded && (
        <Dialog open onOpenChange={(open) => { if (!open) setIsExpanded(false); }}>
          {/* Nothing is layered over the content: no click-to-close scrim, no
              transparent overlay. The point of expanding is to scroll the PDF
              and scrub the video, and either of those would swallow the first
              pointer event. The content box covers the viewport, as in
              fullscreen-media, so closing is the X button or Escape and not a
              click on the dark area. Escape also stops working while focus sits
              inside the cross-origin PDF frame, because its key events never
              reach this document, which is why the X is always visible rather
              than hover-revealed. */}
          <DialogContent className="bg-black/80 backdrop-blur-sm border-none p-0 w-screen h-screen max-h-none max-w-none overflow-hidden rounded-none flex items-center justify-center">
            <DialogHeader className="sr-only">
              <DialogTitle>{`Preview of ${productName}`}</DialogTitle>
              <DialogDescription>{`An expanded view of the ${label.toLowerCase()} preview file the seller attached to this product.`}</DialogDescription>
            </DialogHeader>

            <DialogClose asChild>
              <Button
                variant="outline"
                size="icon"
                className="absolute top-4 right-4 z-50 h-12 w-12 border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white"
                aria-label="Close preview"
              >
                <X className="h-8 w-8" aria-hidden />
              </Button>
            </DialogClose>

            <div className="relative w-full h-full max-w-7xl max-h-[90vh]">
              {renderMedia(true)}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
